/**
 * **演出の器**（Issue #529。索引 #525）。帯（`frame.items`）→ three に渡す `parts`。
 *
 * ## 何をするか
 *
 * 1. 帯の種類（＝ **イベント名**）でテーマの演出を引く（無ければ描かない）
 * 2. 演出の**宣言の層**（`layers`）を、`at`（どこに置くか）から**盤の上 / 舞台のピクセル**へ解く
 * 3. `tf` のキーフレームを**その帯のローカル時間**で解いて、置き方（位置・倍率・回し・濃さ）にする
 * 4. 素材（`parts/`）の `draw` を渡す。**絵は 2D canvas、置き方は器**（盤の層・オブジェクトの層と同じ）
 *
 * ## 時計を持たない
 *
 * 受け取るのは**そのフレームの帯**（`localMs` 付き）だけ。再生位置は `duelEditor/timeline` が持ち、
 * `editorPorts/duelRenderer.js` が写す（`duelEditor/README.md` の守ること 12）。
 *
 * ## コードの層（`script`。Issue #540）
 *
 * `script` は three を受け取って自分で組む層。器は `{ kind: 'script' }` の項目にして runtime へ渡し、
 * runtime が `create({ THREE, kit, …FrameContext })` → 毎フレーム `update(FrameContext)` を呼ぶ。
 * **`FrameContext` はここが組む**（`stage` / `localMs` / `remainingMs` / `payload` / `board` と道具）。
 * `layers` が関数のときも同じ `FrameContext` で呼ぶ。
 */
import { effectKey, effectOfClip, evaluateAt, roleOfType } from '../core/effect';
import { foldHudFx, hudValues } from '../core/hud';
import { CARD_ASPECT } from '../core/field/zones';
import { composeTransform, placeBox, transformOf } from '../core/transform';
import { resolveObjects, resolveStyle } from '../core/theme';
import { partOf } from '../parts';
import ThreeStage from './ThreeStage';

import { cardObjectId } from './ObjectLayer';
import { splitGeometry, withSplit } from './split';

/**
 * **カードを出さない間は motion の関数を呼ばない**（ユーザー判断 2026-10-01）。
 * カード（設定の `objects.card`）は 0 か 1 で、0 のフレームでは **motion（カードを動かす演出）の帯は描く・動かす・HUD に作用する
 * どの関数も呼ばない**。帯そのものはタイムラインに残る（時刻合わせは変わらない）。effect（強調の演出）は関係ない
 */
export const cardHidden = (config) => (resolveObjects(config?.objects).card ?? 1) < 0.5;
const skipClip = (clip, config) => roleOfType(clip?.type).role === 'motion' && cardHidden(config);



/** 盤の上の素材の解像度（`ObjectLayer` と同じ考え方。平面のピクセル 1 に対する画素） */
export const DEFAULT_EFFECT_RESOLUTION = 3;

const num = (v, def = 0) => (Number.isFinite(v) ? v : def);

/** 平面 / 舞台の 4 点 → 中心と大きさ */
function quadBox(quad) {
  if (!Array.isArray(quad) || quad.length !== 4) return null;
  const xs = quad.map((p) => p?.[0]);
  const ys = quad.map((p) => p?.[1]);
  if (![...xs, ...ys].every(Number.isFinite)) return null;
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

/**
 * **`{…}` を payload の値で埋める**（`{ card: '{uid}' }` → その帯の uid、
 * `props: { text: '{phaseLabel}' }` → そのフェイズ名）。
 *
 * **`at` と `props` の両方に効かせる** —— `at` だけに掛けていたので、`props` の
 * `'{phaseLabel}'` が**そのまま文字として画面に出た**（Issue #531）。
 *
 * - 文字列ぜんぶが 1 つの `{…}` なら**値をそのまま返す**（数を文字にしない）。
 *   埋まらなければ `null`（`at` はその層を落とし、文字は空になる）
 * - 文の途中の `{…}` は差し替える（`'TURN {turn}'`）。埋まらないところは空にする
 *
 * **鍵の綴りは問わない**（`{…}` の中は何でも埋め込みとして扱う）—— 英数字に限ると、
 * 綴りを外したものが「埋め込みではない」と見なされて**そのまま画面に出る**。
 * `{…}` が画面に出るのは常に間違いなので、埋まらないものは消すほうが安全。
 */
const HOLE = /\{([^{}]+)\}/g;

export function fillTemplate(value, payload) {
  if (typeof value !== 'string' || !value.includes('{')) return value;
  const whole = /^\{([^{}]+)\}$/.exec(value);
  if (whole) {
    const v = payload?.[whole[1]];
    return v == null ? null : v;
  }
  return value.replace(HOLE, (_, key) => {
    const v = payload?.[key];
    return v == null ? '' : String(v);
  });
}

/**
 * **どこに置くか**を解く。盤の上は**平面の座標**（`zonePlane`）、舞台のピクセルは `zoneQuad` / HUD の箱。
 *
 * **`{ card: uid }` は盤面を引いてゾーンへ写す** —— Field が答えるのはゾーンの場所なので、
 * カードの uid からその居場所を知るには盤面が要る（`core/board::byUid`）。
 *
 * 引けなかったら `at.else` に落ちる（実写の Field は EX デッキ・手札を知らない）。
 */
export function resolveAnchor(at, { view, board, hud, canvas, payload, scene = null }) {
  if (!at || typeof at !== 'object') return null;
  const fallback = () => (at.else ? resolveAnchor(at.else, { view, board, hud, canvas, payload, scene }) : null);

  if ('card' in at) {
    const uid = fillTemplate(at.card, payload);
    const zone = uid ? board?.byUid?.(uid)?.zone : null;
    const box = zone ? quadBox(view?.zonePlane?.(zone)) : null;
    return box ? { space: 'board', box, zone } : fallback();
  }
  if ('zone' in at) {
    const zone = fillTemplate(at.zone, payload);
    const box = zone ? quadBox(view?.zonePlane?.(zone)) : null;
    return box ? { space: 'board', box, zone } : fallback();
  }
  if ('hud' in at) {
    // `turnBadge` は**そのフレームの値**を取る（プレイヤーではない。Issue #540 で直した）
    const arg = at.hud === 'turnBadge' ? hudValues(scene?.hud) : num(at.player, 0);
    const box = hud?.[at.hud]?.(arg);
    return box && box.w > 0 && box.h > 0 ? { space: 'screen', box, zone: null } : fallback();
  }
  if ('screen' in at) {
    const [x, y] = at.screen ?? [];
    if (!Number.isFinite(x) || !Number.isFinite(y)) return fallback();
    // 大きさは素材が決める（素材が名乗らなければ 0 なので描かれない）
    return { space: 'screen', box: { x, y, w: 0, h: 0 }, zone: null };
  }
  return fallback();
}

/**
 * **カードの絵を deps に入れる**（読み込めた瞬間に描き直すため）。
 * 素材が `needsCard` を名乗ったときだけ引く（引くだけで絵が変わらない素材の deps を汚さない）。
 */
function cardDeps(part, props, scope, scene) {
  if (!part?.needsCard) return [];
  const code = props?.code ?? scope?.code ?? null;
  const player = Number.isFinite(props?.player) ? props.player : (scope?.player ?? 0);
  return [scene?.cards?.image?.(code) ?? null, scene?.cards?.sleeve?.(player) ?? null];
}

/**
 * **盤の上の場所 → 使いやすい形**（`script` の `ctx.anchor(at)` が返すもの）。
 *
 * - `space` … `board`（透視・盤の上）/ `screen`（舞台のピクセル）
 * - `world` … 盤なら**ワールドの座標** `[x, 0, z]`（原点は平面の中心・盤は y = 0。runtime と同じ約束）
 * - `screen` … **舞台のピクセル**の中心（盤でも投影して出す＝舞台のピクセルに光を重ねたいとき）
 * - `size` … 大きさ（盤なら平面のピクセル＝ワールドの単位、舞台ならピクセル）
 * - `angle` … 盤の上でのカードの向き（Field の敷き方の分。ラジアン）
 */
export function anchorInfo(at, { view, scene, hud, canvas, payload }) {
  const a = resolveAnchor(at, { view, board: scene?.board, hud, canvas, payload, scene });
  if (!a) return null;
  const cx = a.box.x + a.box.w / 2;
  const cy = a.box.y + a.box.h / 2;
  if (a.space === 'board') {
    const plane = view?.plane ?? { w: 0, h: 0 };
    return {
      space: 'board',
      zone: a.zone,
      world: [cx - plane.w / 2, 0, cy - plane.h / 2],
      plane: [cx, cy],
      screen: view?.toStage?.([cx, cy]) ?? null,
      size: { w: a.box.w, h: a.box.h },
      angle: a.zone ? num(view?.cardAngle?.(a.zone)) : 0,
    };
  }
  return { space: 'screen', zone: null, world: null, plane: null, screen: [cx, cy], size: { w: a.box.w, h: a.box.h }, angle: 0 };
}

/**
 * **`FrameContext`**（`core/effect/types.d.ts`）。`layers` が関数のとき・`script` 層が受け取る。
 * **時刻は帯から**（`localMs` / `remainingMs`）。`stage` は空間だけ。
 */
export function frameContext(clip, { scene, view, canvas, hud, style, payload = null }) {
  const scope = payload ?? clip?.payload ?? {};
  const board = scene?.board ?? null;
  const values = hudValues(scene?.hud);
  const zoneOfCard = (card) => card?.zone ?? (card?.uid ? board?.byUid?.(card.uid)?.zone : null) ?? null;
  const field = {
    known: Boolean(view?.known),
    virtual: Boolean(view?.virtual),
    canvas: view?.canvas ?? canvas,
    plane: view?.plane ?? null,
    unit: num(view?.unit),
    toStage: (pt) => view?.toStage?.(pt) ?? null,
    /** 舞台のピクセル → 平面の座標（平面 y = 0 の上の点。地平線の向こう・分からなければ null。Issue #561） */
    toPlane: (pt) => {
      const q = view?.toPlane?.(pt);
      return Array.isArray(q) && Number.isFinite(q[0]) && Number.isFinite(q[1]) ? q : null;
    },
    zoneQuad: (key) => view?.zoneQuad?.(key) ?? null,
    cardQuad: (card) => { const z = zoneOfCard(card); return z ? (view?.zoneQuad?.(z) ?? null) : null; },
    zonePlane: (key) => view?.zonePlane?.(key) ?? null,
    cardAngle: (key) => num(view?.cardAngle?.(key)),
    outline: view?.outline ?? null,
  };
  const stageHud = {
    life: (p) => hud?.life?.(p) ?? null,
    hand: (p) => hud?.hand?.(p) ?? null,
    plate: (p) => hud?.plate?.(p) ?? null,
    turnBadge: () => hud?.turnBadge?.(values) ?? null,
  };
  const localMs = num(clip?.localMs);
  return {
    stage: { canvas, field, hud: stageHud },
    localMs,
    remainingMs: num(clip?.remainingMs),
    durationMs: localMs + num(clip?.remainingMs),
    payload: scope,
    board,
    /** そのフレームの HUD の値（LP・ターン・プレイヤー） */
    hud: values,
    /** カードの絵（`image(code)` / `sleeve(player)` / `name(code)`）。**素材の調達はページの仕事** */
    cards: scene?.cards ?? null,
    /** 決定論的な乱数の種（同じ帯なら同じ） */
    seed: String(clip?.id ?? ''),
    style: style ?? {},
    /** 宣言と同じ `at` を解く（`anchorInfo`） */
    anchor: (at) => anchorInfo(at, { view, scene, hud, canvas, payload: scope }),
  };
}

/** `script` の組み直しの引き金（カードの絵が読めたら組み直す） */
function scriptDeps(scope, scene) {
  const out = [];
  const img = (code) => scene?.cards?.image?.(code) ?? null;
  if (scope?.code != null) out.push(img(scope.code));
  [scope?.attacker, scope?.target].forEach((c) => { if (c?.code != null) out.push(img(c.code)); });
  (Array.isArray(scope?.cards) ? scope.cards : []).forEach((c) => out.push(img(c?.code)));
  (Array.isArray(scope?.materials) ? scope.materials : []).forEach((c) => out.push(img(c?.code)));
  [0, 1].forEach((p) => out.push(scene?.cards?.sleeve?.(p) ?? null));
  // プレイヤーのアイコン（デッキの絵。ターンの演出が貼る。Issue #550）
  [0, 1].forEach((p) => out.push(scene?.cards?.portrait?.(p) ?? null));
  [0, 1].forEach((p) => out.push(scene?.cards?.playerIcon?.(p) ?? null));
  return out;
}

/** 層の `tf` を帯のローカル時間で解く（キーフレームでも値そのものでも） */
export function transformAt(tf, localMs, base) {
  const out = { ...base };
  Object.entries(tf ?? {}).forEach(([key, value]) => {
    const v = evaluateAt(value, localMs);
    if (Number.isFinite(v)) out[key] = v;
  });
  return out;
}

/**
 * **帯 → three に渡す `parts`**（純関数）。
 *
 * @param theme テーマ（`effects` と `style` を持つ）
 * @param scene そのフレームの値（`board` を使う）
 * @param view 今のカメラでの見え方（`zonePlane` / `unit`）
 * @param canvas 舞台の大きさ
 * @param clips そのフレームに出ている帯 `[{ id, type, localMs, remainingMs, payload, hidden }]`
 * @param config 描画側の設定（`style` の上書き・解像度）
 * @param slot どの舞台の層か（`effects` = HUD の下 / `front` = HUD より手前。層の `front: true` で分かれる）
 */
export function effectStageParts(theme, scene, view, canvas, clips = [], config = null, slot = 'effects') {
  const cfg = config ?? {};
  const style = resolveStyle(theme, cfg.style);
  const res = cfg.effectResolution > 0 ? cfg.effectResolution : DEFAULT_EFFECT_RESOLUTION;
  const hud = theme?.hud?.anchors?.({ canvas, view, options: theme?.hud?.options ?? {}, style });
  const unit = num(view?.unit);
  const out = [];

  (clips ?? []).forEach((clip) => {
    if (clip?.hidden) return;
    if (skipClip(clip, cfg)) return;   // カードを出さない間は motion を呼ばない
    // **帯の型の id（状況 ＋ 役割）で引く**（Issue #628。`core/effect::effectOfClip`）
    const effect = effectOfClip(theme?.effects, clip);
    if (!effect) return;   // **そのテーマに無い演出は出ない**（ファイルを置かないだけで消える）
    const payload = clip.payload ?? {};
    const localMs = num(clip.localMs);
    const ctxFor = (scope) => frameContext(clip, { scene, view, canvas, hud, style, payload: scope });
    /*
     * **層が関数なら FrameContext で組む**（Issue #540）。組めなければその帯は描かない
     * （演出 1 本の失敗で舞台ごと落とさない）。
     */
    let layers = [];
    if (typeof effect.layers === 'function') {
      try { layers = effect.layers(ctxFor(payload)) ?? []; } catch { layers = []; }
    } else {
      layers = effect.layers ?? [];
    }

    /** 解けた層（`parent` が参照する。**宣言の順に解く**ので親は必ず先に居る） */
    const resolved = new Map();

    /*
     * **`for` は配列を展開する**（Issue #534）。`payload` の配列（`cards` / `materials`）の数だけ
     * 層を作り、中の `{…}` は**その要素**から埋める（`{ index }` も使える）。
     * 1 イベントから複数の絵を出すのがこれ（`become_target` は `data.cards` に複数枚来る）。
     */
    const expand = (layer) => {
      if (!layer?.for) return [{ layer, id: layer?.id, item: null, scope: payload }];
      const list = payload?.[layer.for];
      if (!Array.isArray(list) || !list.length) return [];
      return list.map((item, index) => ({
        layer, id: `${layer.id}#${index}`, item,
        scope: { ...payload, ...(item && typeof item === 'object' ? item : { item }), index },
      }));
    };

    /*
     * **HUD より手前の層（`front: true`）は別の舞台に描く**（舞台の層 `front`）。同じ帯の層を
     * 2 つの `EffectLayer` が読み、それぞれ自分の側（`slot`）の層だけを出す。親を持つ層は親と同じ側
     */
    const frontOf = new Map();
    const isFront = (layer) => {
      if (!layer?.id) return false;
      if (!frontOf.has(layer.id)) frontOf.set(layer.id, layer.parent ? Boolean(frontOf.get(layer.parent)) : layer.front === true);
      return frontOf.get(layer.id);
    };
    layers.forEach(isFront);
    layers.flatMap(expand).forEach(({ layer, id: layerId, scope, item }) => {
      if (isFront(layer) !== (slot === 'front')) return;
      if (typeof layer?.script === 'function') {
        /*
         * **コードの層**（Issue #540）。場所も動きも演出が決める —— 器は FrameContext を渡すだけ。
         * `parent` は同じ空間を受け継ぐだけ（コードは自分で置く）。
         */
        const parent = layer.parent ? resolved.get(layer.parent) : null;
        const space = layer.space ?? parent?.at?.space ?? 'screen';
        const frame = { ...ctxFor(scope), item, index: scope?.index ?? 0, seed: `${clip.id}/${layerId}` };
        out.push({
          kind: 'script',
          id: `${clip.id}/${layerId}`,
          space,
          create: layer.script,
          frame,
          // 書体が読めたら組み直す（文字のテクスチャは組むときに焼く。Issue #549）
          deps: [effect, layer.script, scene?.fontReady, ...scriptDeps(scope, scene)],
        });
        return;
      }
      const part = partOf(layer.part);
      if (!part) return;   // **知らない素材は描かない**（綴り間違いで画面が落ちない）

      /*
       * **`parent` はアンカーもトランスフォームも受け継ぐ**（Issue #530）。
       * 親が出ていない（場所が引けなかった・素材が無い）なら**子も出ない** ——
       * 親の居ない子だけが画面の隅に取り残されるのを避ける。
       */
      const parent = layer.parent ? resolved.get(layer.parent) : null;
      if (layer.parent && !parent) return;
      const at = parent ? parent.at : resolveAnchor(layer.at, { view, board: scene?.board, hud, canvas, payload: scope, scene });
      if (!at) return;

      /*
       * **大きさはアンカーが決め、無ければ素材の素の大きさ**（Issue #537）。
       * カードやゾーンに重ねる印はその矩形に合わせたいが、`{ screen: [x, y] }` は**点**なので
       * 矩形が無い —— そこで素材が名乗った大きさに落ちる（名乗っていないと層ごと飛ぶ＝ #531）。
       */
      const size = at.box.w > 0 && at.box.h > 0 ? { w: at.box.w, h: at.box.h } : part.size;
      if (!size) return;
      if (!(size.w > 0) || !(size.h > 0)) return;

      // 部品の名前は**宣言の id**（`for` で増えた分は同じ部品＝同じノブで動く）。`defineEffect` が付ける名前と同じ（`fx:<鍵>/<id>`）
      const partName = `fx:${effectKey(effect)}/${layer.id}`;
      const own = transformAt(layer.tf, localMs, transformOf(style, partName));
      const tf = parent ? composeTransform(parent.tf, own) : own;
      resolved.set(layerId, { at, tf });

      const box = { x: at.box.x + at.box.w / 2 - size.w / 2, y: at.box.y + at.box.h / 2 - size.h / 2, w: size.w, h: size.h };
      const t = placeBox(box, tf, at.space);
      if (!(t.opacity > 0)) return;

      /*
       * **素材のパラメータは 1 つの表にまとめる**（Issue #530）。素材の既定 → テーマの上書き →
       * 層の `props`（**キーフレームを取れる**）の順。置き場を分けると「どっちが効くのか」が増える。
       */
      const props = {};
      Object.entries(layer.props ?? {}).forEach(([k, v]) => {
        // **キーフレームを解いてから `{…}` を埋める**（値が文字列のときだけ効く）
        props[k] = fillTemplate(evaluateAt(v, localMs), scope);
      });
      const partStyle = { ...part.style, ...(cfg.partStyle?.[layer.part] ?? {}), ...props };
      const common = {
        id: `${clip.id}/${layerId}`,
        opacity: t.opacity,
        // **絵は素材、置き方は器**（素材は three もカメラも知らない）
        /*
         * **素材は絵だけを描く**が、カードの絵だけは外から来る（**素材の調達はページの仕事**）。
         * `cards` は `scene.cards`（`image(code)` / `sleeve(player)` / `name(code)`）をそのまま渡す。
         */
        draw: (ctx, env) => part.draw(ctx, { ...env, style: partStyle, params: props, payload: scope, cards: scene?.cards }),
        blend: layer.blend ?? part.blend,
        /*
         * **値で比べる**（object を入れると毎フレーム別物になって描き直しになる）。
         * カードの絵は**読み込めた瞬間に描き直す**必要があるので、引いた絵そのものを入れる。
         */
        deps: [part, size.w, size.h, ...Object.values(partStyle), ...cardDeps(part, props, scope, scene)],
        scale: t.scale,
        rot: t.rot,
      };
      if (at.space === 'screen') {
        out.push({
          ...common,
          kind: 'panel',
          box: { x: t.center[0] - size.w / 2, y: t.center[1] - size.h / 2, w: size.w, h: size.h },
          bitmap: { w: Math.max(1, Math.round(size.w)), h: Math.max(1, Math.round(size.h)) },
        });
        return;
      }
      out.push({
        ...common,
        kind: 'piece',
        center: t.center,
        size,
        // 盤の上での向きは**盤の敷き方の分**（Field）＋ 宣言の回し
        rot: (at.zone ? num(view?.cardAngle?.(at.zone)) : 0) + t.rot,
        lift: num(layer.lift, unit > 0 ? unit * 0.02 : 1),   // 盤の板より少し浮かせる（z ファイティング避け）
        thickness: 0,
        shadow: false,
        bitmap: { w: Math.max(1, Math.round(size.w * res)), h: Math.max(1, Math.round(size.h * res)) },
      });
    });
  });
  return out;
}

/**
 * **盤に出ているカードをそのまま動かす**（R13。Issue #541）。
 *
 * 描いている帯ごとに `effect.tweaks(FrameContext)` を呼び、返ってきた **uid → 変形** を
 * オブジェクトの層の**板の id**（`card:<zone>`）へ写す。渡るのは生の three（`ObjectLayer` の 13d）で、
 * **借りている間はその板は演出のもの・外れたら器が素に戻す**。
 *
 * 同じ板を複数の帯が触るときは**後の帯が勝つ**のではなく**順に重ねる**（先に始まった帯 → 後の帯）。
 * 1 本の演出が投げても舞台は落とさない（その帯のぶんを諦める）。
 *
 * @returns `{ [objectId]: (handle) => void }`（無ければ空）
 */
export function effectTweaks(theme, scene, view, canvas, clips = [], config = null) {
  const cfg = config ?? {};
  const style = resolveStyle(theme, cfg.style);
  const hud = theme?.hud?.anchors?.({ canvas, view, options: theme?.hud?.options ?? {}, style });
  const out = {};
  [...(clips ?? [])]
    .filter((c) => !c?.hidden && !skipClip(c, cfg))
    .sort((a, b) => num(a.startMs) - num(b.startMs))
    .forEach((clip) => {
      const effect = effectOfClip(theme?.effects, clip);
      if (typeof effect?.tweaks !== 'function') return;
      let table = null;
      try { table = effect.tweaks(frameContext(clip, { scene, view, canvas, hud, style })); } catch { table = null; }
      Object.entries(table ?? {}).forEach(([uid, fn]) => {
        if (typeof fn !== 'function') return;
        const zone = scene?.board?.byUid?.(uid)?.zone;
        const id = cardObjectId(zone);
        if (!id) return;
        const prev = out[id];
        out[id] = prev ? (h) => { prev(h); fn(h); } : fn;
      });
    });
  return out;
}

/**
 * **演出が HUD に当てる作用**（Issue #542）。描いている帯ごとに `effect.hud(FrameContext)` を呼び、
 * 部品ごとにまとめる（`core/hud::foldHudFx`。同じ部品は強いほう）。投げた演出は無視する。
 *
 * @returns `{ 'life:0': { shake, flash, pulse, color, phase }, … }`
 */
export function effectHud(theme, scene, view, canvas, clips = [], config = null) {
  const cfg = config ?? {};
  const style = resolveStyle(theme, cfg.style);
  const hud = theme?.hud?.anchors?.({ canvas, view, options: theme?.hud?.options ?? {}, style });
  const list = [];
  (clips ?? []).forEach((clip) => {
    if (clip?.hidden) return;
    if (skipClip(clip, cfg)) return;
    const effect = effectOfClip(theme?.effects, clip);
    if (typeof effect?.hud !== 'function') return;
    try {
      const ctx = frameContext(clip, { scene, view, canvas, hud, style });
      (effect.hud(ctx) ?? []).forEach((e) => list.push({ phase: ctx.localMs, ...e }));
    } catch { /* 1 本の演出で HUD を落とさない */ }
  });
  return foldHudFx(list);
}

/** 2 つの `tweaks` の表を重ねる（同じ板は先 → 後の順に当てる） */
export function mergeTweaks(a, b) {
  const has = (t) => Boolean(t && Object.keys(t).length);
  // **どちらも空なら null**（何も借りていない＝器は素のまま置く）
  if (!has(a)) return has(b) ? b : null;
  if (!has(b)) return a;
  const out = { ...a };
  Object.entries(b).forEach(([id, fn]) => {
    const prev = out[id];
    out[id] = prev ? (h) => { prev(h); fn(h); } : fn;
  });
  return out;
}

/** カードの縦横比（素材が大きさを名乗らないときの目安。`core/field/zones` の語彙） */
export { CARD_ASPECT };

/**
 * **演出の層の後処理**（Issue #549）。テーマの既定（`theme.post(style)`）に、
 * 演出が一時的に足す量（`scene.fxScene.grade` の `bloom` / `chroma`）を足す。どちらも無ければ null。
 */
export function effectPost(theme, style, grade = null) {
  const base = theme?.post?.(style) ?? null;
  const addBloom = Number.isFinite(grade?.bloom) ? grade.bloom : 0;
  const addChroma = Number.isFinite(grade?.chroma) ? grade.chroma : 0;
  if (!base && !addBloom && !addChroma) return null;
  const b = { bloom: 0, threshold: 0.6, radius: 1.4, chroma: 0, ...(base ?? {}) };
  return { ...b, bloom: Math.max(0, b.bloom + addBloom), chroma: Math.max(0, b.chroma + addChroma) };
}

/**
 * **画面を切ってずらす**（Issue #553）の線が通る点を解く。舞台の設定の `split.at` は
 * 舞台のピクセル `[x, y]` か `Anchor`（`{ card }` / `{ zone }` / `{ hud }` / `{ screen }`）か、
 * 2 つの `Anchor` の間（外）`{ from, to, t }`（直接攻撃の「矢印の先」のように、盤の外の点を指すとき）。
 * 盤・オブジェクト・演出の 3 つの層に**同じ値**を渡す（`editorPorts/duelRenderer.js`）。解けなければ null（切らない）。
 */
export function sceneSplit(theme, scene, view, canvas, config = null) {
  const split = scene?.fxScene?.split ?? null;
  if (!split) return null;
  let point = null;
  const style = resolveStyle(theme, config?.style);
  const hud = theme?.hud?.anchors?.({ canvas, view, options: theme?.hud?.options ?? {}, style });
  const info = (at) => anchorInfo(at, { view, scene, hud, canvas, payload: {} });
  if (Array.isArray(split.at)) point = split.at;
  else if (split.at?.from && split.at?.to) {
    // **2 点の間（外）**: `from` → `to` を `t` 倍（1 を超えれば `to` の先）。盤の上なら平面で測ってから写す（透視で歪まない）
    const a = info(split.at.from);
    const b = info(split.at.to);
    const t = num(split.at.t, 1);
    if (a?.plane && b?.plane && view?.toStage) {
      point = view.toStage([a.plane[0] + (b.plane[0] - a.plane[0]) * t, a.plane[1] + (b.plane[1] - a.plane[1]) * t]);
    } else if (a?.screen && b?.screen) {
      point = [a.screen[0] + (b.screen[0] - a.screen[0]) * t, a.screen[1] + (b.screen[1] - a.screen[1]) * t];
    }
  } else if (split.at && typeof split.at === 'object') {
    point = info(split.at)?.screen ?? null;
  } else {
    const c = view?.canvas ?? canvas;
    point = c ? [c.w / 2, c.h / 2] : null;
  }
  return splitGeometry(split, point);
}

/**
 * @param slot `effects`（HUD の下。既定）か `front`（HUD より手前。層の `front: true` だけ）。
 *   **`front` も常駐させる**（出すものが無いフレームで外すと、帯のたびに WebGL のコンテキストを
 *   作っては捨てることになり、ブラウザの上限（16 個ほど）で一番古い＝常駐の舞台が落ちる。
 *   帯の頭で runtime の読み込み待ちの分だけ出ない（pop-in）のも避ける。レビュー 2026-09-28）
 */
export default function EffectLayer({ theme, scene, view, canvas, clips = [], config = null, split = null, slot = 'effects' }) {
  const parts = effectStageParts(theme, scene, view, canvas, clips, config, slot);
  // HUD より手前は HUD と同じで**切らない**（`split` は盤の側の効果）。色の調整は下の演出と同じ
  // **光のにじみ・色収差はプレビューでは切れる**（`config.noPost`。画面を切る＝中身の一部なので残す）
  const glow = config?.noPost ? null : effectPost(theme, resolveStyle(theme, config?.style), scene?.fxScene?.grade ?? null);
  const post = withSplit(glow, slot === 'front' ? null : split);
  return <ThreeStage canvas={view?.canvas ?? canvas} shot={view?.shot} plane={view?.plane} parts={parts} post={post} testId={slot} scale={config?.renderScale ?? 1} />;
}
